import {
  type ElementType,
  forwardRef,
  type HTMLAttributes,
  type ReactElement,
  type ReactNode,
  type Ref,
} from 'react';

import { Dialog, DialogCloseProps } from '@/components/atoms';
import { cn } from '@/lib/utils/tailwind';

import { AsElement } from '@/types';

type ModalCloseProps<T extends ElementType = 'button'> = DialogCloseProps<T>;

const ModalCloseBase = forwardRef<
  HTMLElement,
  {
    as?: ElementType;
    className?: string;
    children?: ReactNode;
  } & Omit<HTMLAttributes<HTMLElement>, 'ref' | 'className' | 'children'>
>(({ as, className, children, ...props }, ref) => {
  const Component: ElementType = as || 'button';

  return (
    <Dialog.Close
      as={Component}
      className={cn(
        ['cursor-pointer', 'rounded-full', 'hover:bg-muted'],
        className,
      )}
      ref={ref}
      {...props}
    >
      {children}
    </Dialog.Close>
  );
});
ModalCloseBase.displayName = 'ModalClose';

const ModalClose = (<T extends ElementType = 'button'>(
  props: ModalCloseProps<T> & {
    ref?: Ref<AsElement<T>>;
  },
): ReactElement => {
  return <ModalCloseBase {...props} />;
}) as <T extends ElementType = 'button'>(
  props: ModalCloseProps<T> & {
    ref?: Ref<AsElement<T>>;
  },
) => ReactElement;

Object.defineProperty(ModalClose, 'displayName', {
  value: 'ModalClose',
  configurable: true,
});

export { ModalClose, type ModalCloseProps };
